import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Send, MessageCircle, Loader2, Paperclip, X, FileText, Bell, BellOff, Image as ImageIcon, ArrowLeft, Camera, FolderOpen, ChevronRight, Archive, Check, CheckCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Sheet, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { PortalSheetContent } from '../PortalSheetContent';
import { usePortalDestination, usePortalDraft } from '../PortalDestinationContext';
import { PortalRequestDestination } from '../PortalIdentity';
import { useChatReadReceipt } from '../lib/useChatReadReceipt';
import { ChatArchiveControls } from '../ChatArchiveControls';
import { PortalEmptyState } from '../ui/PortalEmptyState';
import { EmojiPicker } from '../../components/EmojiPicker';
import { ChatAttachmentView } from '../../components/ChatAttachmentView';
import { QuickReactionPicker } from '../../components/QuickReactionPicker';

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf', 'text/plain'];

function urlSafeBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const outputArray = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) outputArray[i] = raw.charCodeAt(i);
  return outputArray;
}


function dayDivider(iso, zone) {
  const d = new Date(iso);
  const key = v => v.toLocaleDateString('en-CA', { timeZone: zone });
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400000);
  if (key(d) === key(today)) return 'Today';
  if (key(d) === key(yesterday)) return 'Yesterday';
  return d.toLocaleDateString([], { timeZone: zone, weekday: 'short', day: 'numeric', month: 'short' });
}

function ChatRow({ thread, onOpen }) {
  const preview = thread.last_message_preview
    ? `${thread.last_message_sender === 'patient' ? 'You: ' : ''}${thread.last_message_preview}`
    : 'No messages yet';
  return (
    <button type="button" className="portal-chat-row" onClick={() => onOpen(thread.thread_id)} data-testid={`portal-chat-row-${thread.thread_id}`}>
      <div className="portal-chat-row-avatar" aria-hidden="true">{(thread.branch_name || '?').charAt(0).toUpperCase()}</div>
      <div className="min-w-0 flex-1 text-left">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold truncate" data-testid={`portal-chat-row-branch-${thread.thread_id}`}>{thread.branch_name}</span>
          {thread.last_message_at && (
            <span className="text-xs text-slate-400 shrink-0">{new Date(thread.last_message_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span className="text-sm text-slate-500 truncate" data-testid={`portal-chat-row-preview-${thread.thread_id}`}>{preview}</span>
          {thread.unread_by_patient > 0 && (
            <span className="portal-chat-row-unread" data-testid={`portal-chat-row-unread-${thread.thread_id}`}>
              {thread.unread_by_patient > 99 ? '99+' : thread.unread_by_patient}
            </span>
          )}
        </div>
      </div>
      <ChevronRight size={16} className="portal-rx-chevron" />
    </button>
  );
}

export default function PortalChatPage() {
  const { api, branch, destinationLabel, ensureConnection } = usePortalDestination();
  const [messages, setMessages] = useState([]);
  const [threads, setThreads] = useState([]);
  const [threadId, setThreadId] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [conversation, setConversation] = useState(null);
  const [view, setView] = useState('active');
  const [dates, setDates] = useState([]);
  const [date, setDate] = useState('');
  const [zone, setZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [page, setPage] = useState(0);
  const [datePage, setDatePage] = useState(0);
  const [moreDates, setMoreDates] = useState(false);
  const [readRevision, setReadRevision] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  // Attachment state
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachSheetOpen, setAttachSheetOpen] = useState(false);
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);
  const filesInputRef = useRef(null);
  // Push notification state
  const [pushStatus, setPushStatus] = useState('unknown'); // unknown | granted | denied | subscribed
  const scrollRef = useRef(null);
  const readMarkerRef = useRef(null);
  const textareaRef = useRef(null);
  const stickToLatest = useRef(true);
  const selectedThread = useRef('');
  const composingNew = useRef(false);
  const sequence = useRef(0);
  usePortalDraft(draft.trim(), sending, draft.trim()
    ? `your message "${draft.trim().length > 60 ? `${draft.trim().slice(0, 60)}…` : draft.trim()}"`
    : '');
  const onRead = useCallback((id, remaining) => { setThreads(rows => rows.map(t => t.thread_id === id ? { ...t, unread_by_patient: remaining } : t)); setReadRevision(v => v + 1); }, []);
  useChatReadReceipt(conversation, readMarkerRef, onRead);

  useEffect(() => { api.get('/health/settings').then(r => { if (r.data.timezone) setZone(r.data.timezone); }).catch(() => {}); }, [api]);
  useEffect(() => {
    // Check current notification permission
    if ('Notification' in window) {
      setPushStatus(Notification.permission === 'granted' ? 'granted' : Notification.permission === 'denied' ? 'denied' : 'unknown');
    }
  }, []);

  const changeView = v => { setConversation(null); setMessages([]); setPage(0); setView(v); setAttachSheetOpen(false); };
  const changeDate = v => { setConversation(null); setMessages([]); setPage(0); setDate(v); };
  const changeZone = async v => {
    try { await api.put('/health/settings', { timezone: v }); setZone(v); setDate(''); setPage(0); setDatePage(0); }
    catch { toast.error('Could not save time zone'); }
  };

  const load = useCallback(async () => {
    if (document.visibilityState !== 'visible') return;
    const seq = ++sequence.current;
    try {
      const list = await api.get('/chat/threads');
      const rows = list.data.threads || [];
      const selected = composingNew.current ? null : (rows.find(t => t.thread_id === (threadId || selectedThread.current)) || rows[0]);
      let selectedDate = date;
      if (selected && view === 'archive') {
        const groups = await api.get('/chat/archive-dates', { params: { thread_id: selected.thread_id, zone, page: datePage } });
        if (sequence.current !== seq) return;
        setDates(groups.data.dates); setMoreDates(groups.data.has_more);
        selectedDate = groups.data.dates.some(d => d.date === date) ? date : groups.data.dates[0]?.date || '';
        setDate(selectedDate);
      }
      const r = selected && (view === 'active' || selectedDate) ? await api.get('/chat/messages', { params: { thread_id: selected.thread_id, view, date: view === 'archive' ? selectedDate : undefined, zone, page } }) : null;
      if (sequence.current !== seq) return;
      selectedThread.current = selected?.thread_id || '';
      setThreads(rows); setMessages(r?.data.messages || []); setConversation(r?.data || null); setError('');
    } catch { if (sequence.current === seq) setError('Could not load conversations. Please try again.'); }
    finally { if (sequence.current === seq) setLoading(false); }
  }, [api, threadId, view, date, zone, page, datePage]);

  useEffect(() => {
    load();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 20000);
    window.addEventListener('focus', load);
    window.addEventListener('online', load);
    document.addEventListener('visibilitychange', load);
    return () => {
      clearInterval(timer); sequence.current += 1;
      window.removeEventListener('focus', load);
      window.removeEventListener('online', load);
      document.removeEventListener('visibilitychange', load);
    };
  }, [load, readRevision]);

  useEffect(() => {
    if (scrollRef.current && stickToLatest.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) { textareaRef.current.style.height = 'auto'; textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`; }
  }, [draft]);

  const openThread = (id) => {
    composingNew.current = false; stickToLatest.current = true; setMessages([]); setConversation(null); setLoading(true);
    setError(''); setPage(0); setDate(''); setDatePage(0); setThreadId(id); setDetailOpen(true);
  };
  const startNewConversation = () => {
    composingNew.current = true; stickToLatest.current = true; setMessages([]); setConversation(null); setLoading(false);
    setError(''); setPage(0); setDate(''); setDatePage(0); setThreadId(''); setView('active'); setDetailOpen(true);
  };
  const backToList = () => setDetailOpen(false);

  // ─── Attachment handling ───────────────────────────────────────────────────

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) { toast.error('Unsupported file type. Use JPEG, PNG, WebP, GIF, PDF, or TXT'); return; }
    if (file.size > MAX_FILE_BYTES) { toast.error('File too large — maximum 10 MB'); return; }

    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/chat/attachment', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPendingAttachment(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingAttachment(false);
      e.target.value = '';
    }
  };

  // ─── Push notifications ────────────────────────────────────────────────────

  const subscribeToPush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }
    try {
      const vapidKeyRes = await api.get('/push/vapid-public-key');
      const vapidKey = vapidKeyRes.data.vapid_public_key;
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlSafeBase64ToUint8Array(vapidKey),
      });
      await api.post('/push/subscribe', sub.toJSON());
      setPushStatus('subscribed');
      toast.success('Push notifications enabled!');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setPushStatus('denied');
        toast.error('Notification permission denied. Enable it in your browser settings.');
      } else {
        toast.error('Could not enable notifications');
        console.error(err);
      }
    }
  };

  const unsubscribeFromPush = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api.delete('/push/unsubscribe', { data: { endpoint: sub.endpoint } });
          await sub.unsubscribe();
        }
      }
      setPushStatus('unknown');
      toast.success('Notifications disabled');
    } catch { toast.error('Could not disable notifications'); }
  };

  // ─── Send message ──────────────────────────────────────────────────────────

  const send = async (e) => {
    e.preventDefault();
    if ((!draft.trim() && !pendingAttachment) || !branch) return;
    setSending(true);
    try {
      if (!await ensureConnection()) return;
      const payload = { body: draft.trim(), tenant_id: branch.tenant_id };
      if (pendingAttachment) payload.attachments = [pendingAttachment];
      const r = await api.post('/chat/messages', payload);
      setConfirmation(`Message sent to ${r.data.branch_name || branch.name}.`);
      setTimeout(() => setConfirmation(''), 4000);
      composingNew.current = false; selectedThread.current = r.data.thread_id; setThreadId(r.data.thread_id);
      setDraft(''); setPendingAttachment(null); stickToLatest.current = true; await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };
  const sendOnEnter = (e) => { if (e.key === 'Enter' && !e.shiftKey) { send(e); } };

  const reactToMessage = async (messageId, emoji) => {
    try {
      const res = await api.post(`/chat/messages/${messageId}/react`, { emoji });
      setMessages(prev => prev.map(m => m.message_id === messageId ? { ...m, reactions: res.data.reactions } : m));
    } catch (err) { toast.error('Could not update reaction'); }
  };

  const openThreadData = conversation ? threads.find(t => t.thread_id === conversation.thread_id) : null;
  const headerBranchLabel = openThreadData?.branch_name || destinationLabel || 'New conversation';

  // ─── Conversation detail (full-screen) ──────────────────────────────────────
  if (detailOpen) {
    let lastDay = '';
    return (
      <div className="portal-flow portal-chat-detail" data-testid="portal-chat-detail">
        <div className="portal-flow-header portal-chat-detail-header">
          <button type="button" onClick={backToList} aria-label="Back to conversations" data-testid="portal-chat-back"><ArrowLeft size={20} /></button>
          <span className="pwa-head-avatar" aria-hidden="true">{(headerBranchLabel || '?').trim().charAt(0).toUpperCase()}</span>
          <h1 className="truncate" data-testid="portal-chat-detail-branch">{headerBranchLabel}</h1>
          {'Notification' in window && pushStatus !== 'denied' ? (
            <button type="button" onClick={pushStatus === 'subscribed' ? unsubscribeFromPush : subscribeToPush}
              title={pushStatus === 'subscribed' ? 'Disable push notifications' : 'Enable push notifications'}
              data-testid="push-notification-toggle">
              {pushStatus === 'subscribed' ? <BellOff size={18} /> : <Bell size={18} />}
            </button>
          ) : <span aria-hidden="true" />}
        </div>

        {!branch && (
          <div className="px-0 pb-2"><PortalRequestDestination testId="portal-chat-destination" /></div>
        )}

        <ChatArchiveControls {...{ view, changeView, dates, date, changeDate, zone, changeZone, page, datePage, moreDates }}
          setPage={v => { setConversation(null); setPage(v); }}
          setDatePage={v => { setDate(''); setDatePage(v); }}
          hasMore={conversation?.has_more} />

        {error && <p role="alert" data-testid="portal-chat-error" className="text-red-700 text-sm">{error}</p>}
        {confirmation && <p role="status" data-testid="portal-chat-confirmation" className="text-emerald-800 text-sm">{confirmation}</p>}

        <div ref={scrollRef}
          onScroll={e => { const el = e.currentTarget; stickToLatest.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48; }}
          className="portal-chat-scroll pwa-scroll" data-testid="portal-chat-scroll">
          {loading && <Loader2 className="w-5 h-5 animate-spin mx-auto" aria-label="Loading conversations" />}
          {!loading && messages.length === 0 && (
            <p className="text-center text-sm text-slate-500 py-10" data-testid="portal-chat-empty">No messages at this location yet.</p>
          )}
          {messages.map(m => {
            const mine = m.sender === 'patient';
            const dayKey = new Date(m.created_at).toLocaleDateString('en-CA', { timeZone: zone });
            const newDay = dayKey !== lastDay;
            lastDay = dayKey;
            return (
              <div key={m.message_id} className="pwa-group">
                {newDay && <p className="pwa-day" data-testid={`portal-chat-day-${dayKey}`}>{dayDivider(m.created_at, zone)}</p>}
                <div className={`pwa-line ${mine ? 'pwa-line-mine' : ''}`} data-testid={`portal-chat-message-${m.message_id}`}>
                  <div className={`pwa-bubble ${mine ? 'pwa-bubble-mine' : 'pwa-bubble-theirs'}`}>
                    {!mine && <div className="pwa-sender">{m.sender_name || 'Pharmacy'}</div>}
                    {m.body && <div className="pwa-text">{m.body}</div>}
                    {m.attachments?.map((att, i) => (
                      <ChatAttachmentView key={i} attachment={att} api={api} testId={`portal-chat-attachment-${m.message_id}-${i}`} />
                    ))}
                    <span className="pwa-meta">
                      {new Date(m.created_at).toLocaleTimeString([], { timeZone: zone, hour: 'numeric', minute: '2-digit' })}
                      {mine && (m.read_at
                        ? <CheckCheck size={13} className="pwa-tick pwa-tick-read" data-testid={`portal-chat-read-${m.message_id}`} />
                        : <Check size={13} className="pwa-tick" />)}
                    </span>
                  </div>
                  {view === 'active' && (
                    <QuickReactionPicker
                      current={m.reactions?.patient}
                      onReact={(emoji) => reactToMessage(m.message_id, emoji)}
                      testId={`portal-chat-reaction-${m.message_id}`}
                    />
                  )}
                </div>
              </div>
            );
          })}
          <div ref={readMarkerRef} className="h-px" aria-hidden="true" data-testid="portal-chat-read-marker" />
        </div>

        {view === 'archive' ? (
          <div className="portal-chat-composer flex items-center justify-between gap-3 text-sm">
            <span data-testid="portal-chat-archive-read-only">Archived · read-only</span>
            <Button variant="outline" data-testid="portal-chat-continue-active" onClick={() => changeView('active')}>Continue in Active</Button>
          </div>
        ) : (
          <div className="portal-chat-composer">
            {!branch && (
              <p className="text-xs text-amber-700 mb-2" data-testid="portal-chat-no-branch-prompt">
                Choose a pharmacy and branch above before sending a message.
              </p>
            )}
            {pendingAttachment && (
              <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
                {pendingAttachment.content_type?.startsWith('image/') ? <ImageIcon className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                <span className="truncate max-w-[200px]">{pendingAttachment.filename}</span>
                <span className="text-slate-400">({(pendingAttachment.size / 1024).toFixed(1)} KB)</span>
                <button type="button" onClick={() => setPendingAttachment(null)} className="text-red-500 hover:text-red-700 ml-auto" data-testid="remove-attachment-btn">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <form onSubmit={send} className="pwa-composer-row flex items-end gap-2">
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} data-testid="chat-camera-input" />
              <input ref={libraryInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} data-testid="chat-library-input" />
              <input ref={filesInputRef} type="file" accept={ALLOWED_TYPES.join(',')} className="hidden" onChange={handleFileSelect} data-testid="chat-file-input" />
              <Button type="button" variant="ghost" size="icon" onClick={() => setAttachSheetOpen(true)}
                disabled={!branch || uploadingAttachment || !!pendingAttachment} title="Add an attachment"
                className="shrink-0" data-testid="chat-attach-btn">
                {uploadingAttachment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              </Button>
              <EmojiPicker testId="portal-chat-emoji" disabled={!branch} onSelect={(emoji) => { setDraft(d => d + emoji); textareaRef.current?.focus(); }} />
              <Textarea ref={textareaRef} rows={1} value={draft} disabled={!branch} maxLength={2000}
                onChange={e => setDraft(e.target.value)} onKeyDown={sendOnEnter}
                placeholder={branch ? 'Type a message…' : 'Select a branch first'}
                className="portal-chat-input resize-none" data-testid="portal-chat-input" />
              <Button type="submit" disabled={sending || (!draft.trim() && !pendingAttachment) || !branch}
                className="portal-primary shrink-0 pwa-send" aria-label="Send message" data-testid="portal-chat-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </div>
        )}

        <Sheet open={attachSheetOpen} onOpenChange={setAttachSheetOpen}>
          <PortalSheetContent side="bottom" className="portal-mobile-sheet" data-testid="chat-attach-sheet">
            <div className="portal-sheet-handle" aria-hidden="true" />
            <SheetTitle>Add an attachment</SheetTitle>
            <SheetDescription className="sr-only">Choose camera, photo library, or files</SheetDescription>
            <div className="space-y-2 mt-1">
              <button type="button" className="portal-flow-choice" data-testid="chat-attach-camera"
                onClick={() => { setAttachSheetOpen(false); cameraInputRef.current?.click(); }}>
                <Camera size={20} /> Camera
              </button>
              <button type="button" className="portal-flow-choice" data-testid="chat-attach-library"
                onClick={() => { setAttachSheetOpen(false); libraryInputRef.current?.click(); }}>
                <ImageIcon size={20} /> Photo Library
              </button>
              <button type="button" className="portal-flow-choice" data-testid="chat-attach-files"
                onClick={() => { setAttachSheetOpen(false); filesInputRef.current?.click(); }}>
                <FolderOpen size={20} /> Files
              </button>
            </div>
          </PortalSheetContent>
        </Sheet>
      </div>
    );
  }

  // ─── Conversation list ────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="portal-chat-page">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><MessageCircle className="w-6 h-6 shrink-0 text-emerald-600" />Chat</h1>
        <Button size="sm" className="portal-primary" onClick={startNewConversation} data-testid="portal-chat-new">New message</Button>
      </div>

      <PortalRequestDestination testId="portal-chat-destination" />

      <div role="tablist" aria-label="Message history" className="portal-segments" data-testid="portal-chat-list-segments">
        {[['active', MessageCircle, 'Active'], ['archive', Archive, 'Archived']].map(([key, Icon, label]) => (
          <button key={key} role="tab" aria-selected={view === key} className={view === key ? 'selected' : ''}
            onClick={() => changeView(key)} data-testid={`portal-chat-list-view-${key}`}>
            <Icon size={16} />{label}
          </button>
        ))}
      </div>

      {error && <p role="alert" data-testid="portal-chat-list-error" className="text-red-700 text-sm">{error}</p>}

      {loading && <Loader2 className="w-5 h-5 animate-spin mx-auto" aria-label="Loading conversations" />}

      {!loading && threads.length === 0 && (
        <PortalEmptyState icon={MessageCircle} title="No conversations yet"
          description="Start a conversation with your pharmacist and it will show up here."
          action={<Button className="portal-primary mt-2" onClick={startNewConversation} data-testid="portal-chat-start-empty">Start a conversation</Button>} />
      )}

      {!loading && threads.length > 0 && (
        <div className="space-y-2" data-testid="portal-chat-list">
          {threads.map(t => <ChatRow key={t.thread_id} thread={t} onOpen={openThread} />)}
        </div>
      )}
    </div>
  );
}
